import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import AppointmentCalendar from '../../components/feature/AppointmentCalendar';
import { useCart } from '../../contexts/CartContext';
import { STORAGE_KEY, DURATION } from '../membership/components/CountdownTimer';

export default function BookPage() {
  const navigate = useNavigate();
  const { items } = useCart();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedTime, setSelectedTime] = useState<string | undefined>();
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [timerActive, setTimerActive] = useState(false);

  useEffect(() => {
    const startTime = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
    if (!startTime || isNaN(startTime)) {
      setTimerActive(false);
      return;
    }
    const update = () => {
      const elapsed = Date.now() - startTime;
      const remaining = DURATION - elapsed;
      if (remaining <= 0) {
        setTimeLeft(0);
        setTimerActive(false);
      } else {
        setTimeLeft(remaining);
        setTimerActive(true);
      }
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  const minutes = Math.floor(timeLeft / 60000);
  const seconds = Math.floor((timeLeft % 60000) / 1000);

  const handleDateTimeSelect = (date: Date, time: string) => {
    setSelectedDate(date);
    setSelectedTime(time);
  };

  const handleContinueToCheckout = () => {
    navigate('/checkout', {
      state: {
        appointmentDate: selectedDate,
        appointmentTime: selectedTime,
      },
    });
  };

  const hasItemsInCart = items.length > 0;

  return (
    <div className="min-h-screen bg-sand">
      <Navigation />
      <section className="pt-32 pb-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">

          {/* Page Header */}
          <div className="text-center mb-10">
            <h1 className="text-5xl md:text-6xl font-light text-charcoal mb-4 tracking-wide">
              Book Your Appointment
            </h1>
            <p className="text-greige text-lg font-light leading-relaxed max-w-xl mx-auto">
              Choose your preferred date and time — your session will be confirmed instantly.
            </p>
          </div>

          {/* 15% Off Timer Banner */}
          {timerActive && (
            <div className="max-w-2xl mx-auto mb-10">
              <div className="bg-gradient-to-r from-gold/10 via-gold/15 to-gold/10 border border-gold/40 rounded-2xl px-8 py-6 text-center shadow-sm">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <i className="ri-time-line text-gold text-xl"></i>
                  <p className="text-base md:text-lg font-light text-charcoal tracking-wide">
                    If you book within <strong className="text-gold font-semibold">10 minutes</strong>, get{' '}
                    <strong className="text-gold font-semibold">15% off</strong> your membership
                  </p>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <div className="flex flex-col items-center bg-white border border-gold/30 rounded-xl px-5 py-3 min-w-[64px] shadow-sm">
                    <span className="text-3xl font-light text-charcoal tabular-nums leading-none">
                      {String(minutes).padStart(2, '0')}
                    </span>
                    <span className="text-[10px] tracking-widest text-greige uppercase mt-1">min</span>
                  </div>
                  <span className="text-2xl font-light text-gold mb-2">:</span>
                  <div className="flex flex-col items-center bg-white border border-gold/30 rounded-xl px-5 py-3 min-w-[64px] shadow-sm">
                    <span className="text-3xl font-light text-charcoal tabular-nums leading-none">
                      {String(seconds).padStart(2, '0')}
                    </span>
                    <span className="text-[10px] tracking-widest text-greige uppercase mt-1">sec</span>
                  </div>
                </div>
                <p className="text-xs text-greige font-light mt-3 tracking-wide">
                  Discount applied automatically at checkout
                </p>
              </div>
            </div>
          )}

          {hasItemsInCart ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

              {/* Calendar Column */}
              <div className="lg:col-span-2">
                <div className="bg-bone rounded-2xl p-8 lg:p-10 mb-6">
                  <h2 className="text-2xl font-light text-charcoal mb-1 tracking-wide">
                    Choose Your Date &amp; Time
                  </h2>
                  <p className="text-greige text-sm font-light mb-8">
                    Select an available date, then pick a time slot below
                  </p>
                  <AppointmentCalendar
                    onSelectDateTime={handleDateTimeSelect}
                    selectedDate={selectedDate}
                    selectedTime={selectedTime}
                  />
                </div>

                {selectedDate && selectedTime && (
                  <div className="space-y-4">
                    {timerActive && (
                      <div className="bg-gold/10 border border-gold/30 rounded-xl px-6 py-4 flex items-center gap-3">
                        <i className="ri-price-tag-3-line text-gold text-lg"></i>
                        <p className="text-sm font-light text-charcoal">
                          <strong className="text-gold">15% discount</strong> will be applied at checkout — timer still running!
                        </p>
                      </div>
                    )}
                    <button
                      onClick={handleContinueToCheckout}
                      className="w-full py-4 bg-charcoal text-white text-base font-light tracking-wide rounded-full hover:bg-charcoal/80 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-sm"
                    >
                      Continue to Checkout
                      <i className="ri-arrow-right-line ml-2"></i>
                    </button>
                  </div>
                )}
              </div>

              {/* Sidebar: Cart Summary */}
              <div className="lg:col-span-1">
                <div className="bg-bone rounded-2xl p-8 sticky top-24">
                  <h3 className="text-xl font-light text-charcoal mb-6 tracking-wide">
                    Your Selection
                  </h3>

                  <div className="space-y-4 mb-6">
                    {items.map((item) => (
                      <div key={item.id} className="pb-4 border-b border-greige/15 last:border-0">
                        <p className="text-base font-light text-charcoal mb-1">{item.name}</p>
                        <p className="text-xs text-greige font-light mb-2">{item.ivCount}</p>
                        {timerActive && item.discountedPrice ? (
                          <div className="flex items-center gap-2">
                            <span className="text-base font-light text-gold">{item.discountedPrice}</span>
                            <span className="text-xs text-greige line-through">{item.price}</span>
                            <span className="text-[10px] bg-gold/20 text-gold px-1.5 py-0.5 rounded-full font-medium">15% OFF</span>
                          </div>
                        ) : (
                          <span className="text-base font-light text-gold">{item.price}</span>
                        )}
                        <span className="text-xs text-greige font-light">{item.period}</span>
                      </div>
                    ))}
                  </div>

                  {/* Steps Guide */}
                  <div className="pt-4 border-t border-greige/15">
                    <p className="text-xs text-greige uppercase tracking-widest mb-4 font-light">Booking Steps</p>
                    <div className="space-y-3">
                      {[
                        { step: '1', label: 'Select a date', done: !!selectedDate },
                        { step: '2', label: 'Pick a time slot', done: !!selectedTime },
                        { step: '3', label: 'Checkout & pay', done: false },
                        { step: '4', label: 'Confirmation', done: false },
                      ].map(({ step, label, done }) => (
                        <div key={step} className="flex items-center gap-3">
                          <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-light flex-shrink-0 ${done ? 'bg-gold text-white' : 'bg-greige/20 text-greige'}`}>
                            {done ? <i className="ri-check-line text-sm"></i> : step}
                          </div>
                          <span className={`text-sm font-light ${done ? 'text-charcoal' : 'text-greige'}`}>{label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          ) : (
            <div className="max-w-2xl mx-auto text-center py-16">
              <div className="bg-bone rounded-2xl p-12">
                <div className="w-20 h-20 flex items-center justify-center bg-gold/10 rounded-full mx-auto mb-6">
                  <i className="ri-calendar-line text-4xl text-gold"></i>
                </div>
                <h2 className="text-2xl font-light text-charcoal mb-4 tracking-wide">
                  No Membership Selected
                </h2>
                <p className="text-greige text-base font-light mb-8">
                  Please select a membership package before booking your appointment
                </p>
                <a
                  href="/membership"
                  className="inline-block py-3 px-8 bg-charcoal text-white text-base font-light tracking-wide rounded-full hover:bg-charcoal/80 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  View Memberships
                </a>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
